// =============================================================================
// Authentication Service
// =============================================================================

import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '../config/api.js';

class AuthService {
  constructor() {
    this.baseURL = '/api/auth';
  }

  // Login with username/email and password
  async login(credentials) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN, credentials);
      return response.data.data; // Extract the actual data from the success response
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Login failed');
    }
  }

  // Register new user
  async register(userData) {
    try {
      const response = await apiClient.post(`${this.baseURL}/register`, userData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  }

  // Logout
  async logout() {
    try {
      await apiClient.post(`${this.baseURL}/logout`);
    } catch (error) {
      // Logout should always succeed locally even if server call fails
      console.warn('Logout server call failed:', error);
    }
  }

  // Verify token
  async verifyToken(token) {
    try {
      const response = await apiClient.get(`${this.baseURL}/verify`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data.data; // Extract the actual data from the success response
    } catch (error) {
      return null;
    }
  }

  // Refresh token
  async refreshToken() {
    try {
      const response = await apiClient.post(`${this.baseURL}/refresh`);
      return response.data.token;
    } catch (error) {
      throw new Error('Token refresh failed');
    }
  }

  // Get current user profile
  async getProfile() {
    try {
      const response = await apiClient.get(`${this.baseURL}/profile`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get profile');
    }
  }

  // Update user profile
  async updateProfile(updates) {
    try {
      const response = await apiClient.put(`${this.baseURL}/profile`, updates);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  }

  // Change password
  async changePassword(currentPassword, newPassword) {
    try {
      await apiClient.post(`${this.baseURL}/change-password`, {
        currentPassword,
        newPassword
      });
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to change password');
    }
  }

  // Validate username availability
  async validateUsername(username) {
    try {
      const response = await apiClient.get(`${this.baseURL}/validate-username`, {
        params: { username }
      });
      return response.data.available;
    } catch (error) {
      return false;
    }
  }

  // Validate email availability
  async validateEmail(email) {
    try {
      const response = await apiClient.get(`${this.baseURL}/validate-email`, {
        params: { email }
      });
      return response.data.available;
    } catch (error) {
      return false;
    }
  }

  // Request password reset
  async requestPasswordReset(email) {
    try {
      await apiClient.post(`${this.baseURL}/request-password-reset`, { email });
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to request password reset');
    }
  }

  // Reset password with token
  async resetPassword(token, newPassword) {
    try {
      await apiClient.post(`${this.baseURL}/reset-password`, {
        token,
        newPassword
      });
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reset password');
    }
  }

  // Enable two-factor authentication
  async enableTwoFactor() {
    try {
      const response = await apiClient.post(`${this.baseURL}/2fa/enable`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to enable 2FA');
    }
  }

  // Disable two-factor authentication
  async disableTwoFactor(code) {
    try {
      await apiClient.post(`${this.baseURL}/2fa/disable`, { code });
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to disable 2FA');
    }
  }

  // Verify two-factor code
  async verifyTwoFactor(code) {
    try {
      const response = await apiClient.post(`${this.baseURL}/2fa/verify`, { code });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Invalid 2FA code');
    }
  }

  // Get user sessions
  async getSessions() {
    try {
      const response = await apiClient.get(`${this.baseURL}/sessions`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get sessions');
    }
  }

  // Revoke session
  async revokeSession(sessionId) {
    try {
      await apiClient.delete(`${this.baseURL}/sessions/${sessionId}`);
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to revoke session');
    }
  }

  // Revoke all sessions except current
  async revokeAllSessions() {
    try {
      await apiClient.delete(`${this.baseURL}/sessions`);
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to revoke sessions');
    }
  }
}

// Create and export singleton instance
export const authService = new AuthService();
